import {
  BALKEN_BREITE,
  BALKEN_HOEHE,
  RING_BREITE,
  RING_MITTE,
  RING_R,
  RING_UMFANG,
  type RingSegment,
} from '../lib/expenseChart'

/**
 * Die beiden Ausgabendiagramme als Zeichnung: ein Ring und ein Balken.
 *
 * Dieselben zwei Bauteile stehen in der Budgetansicht und im gedruckten Buch.
 * Vorher gab es sie zweimal - am Bildschirm von einer Diagrammbibliothek, auf
 * dem Papier von Hand gezeichnet - und die beiden Fassungen konnten
 * auseinanderlaufen, ohne dass es jemandem auffiel. Die Rechnung dahinter steht
 * in lib/expenseChart.ts, gezeichnet wird sie hier, und beide Ansichten
 * benutzen dieses eine Paar.
 *
 * Alles daran ist SVG mit Fuellungen im Vordergrund und keine Hintergrundfarbe.
 * Der Unterschied entscheidet, ob es überhaupt gedruckt wird: Hintergrundfarben
 * lässt der Browser nur aufs Papier, wenn im Druckdialog "Hintergrundgrafiken"
 * angehakt ist, Fuellungen und Striche in einem SVG-Element dagegen immer. Ein
 * Ringdiagramm, das je nach Haken da ist oder nicht, wäre im gebundenen Buch
 * ein Aergernis.
 *
 * Am Bildschirm darf mehr sein als auf dem Papier - hervorheben, Werte
 * einblenden. Deshalb sind die Zutaten dafuer optional: ohne sie entsteht genau
 * die Zeichnung, die ins Buch gehört.
 */

interface RingProps {
  /** Die Segmente aus ringSegments(). */
  segmente: readonly RingSegment[]
  /**
   * Praefix der Klassennamen: "dr-ring" im Buch, "ring" am Bildschirm.
   *
   * Die Zeichnung ist dieselbe, gesetzt wird sie verschieden - im Buch auf
   * 45 mm neben einer Zahlenliste, am Bildschirm in einer Kachel.
   */
  praefix: string
  /** Beschriftung des Bildes für alle, die es nicht sehen. */
  label: string
  /** Die grosse Zahl in der Mitte. */
  wert: string
  /** Die kleine Zeile darunter. */
  text: string
  /** Nur am Bildschirm: welches Segment gerade hervorgehoben ist. */
  hervor?: string
  /** Nur am Bildschirm: der Zeiger betritt ein Segment ("" beim Verlassen). */
  onZeigen?: (key: string) => void
}

export function ExpenseRing({ segmente, praefix, label, wert, text, hervor, onZeigen }: RingProps) {
  return (
    <svg
      className={praefix}
      viewBox={`0 0 ${String(RING_MITTE * 2)} ${String(RING_MITTE * 2)}`}
      role="img"
      aria-label={label}
    >
      {/*
        Gedreht, damit das erste Segment oben anfaengt: ein Kreis beginnt bei
        drei Uhr, gelesen wird ein Ring aber ab zwoelf.
      */}
      <g transform={`rotate(-90 ${String(RING_MITTE)} ${String(RING_MITTE)})`}>
        <circle
          cx={RING_MITTE}
          cy={RING_MITTE}
          r={RING_R}
          fill="none"
          stroke="var(--rule)"
          strokeWidth={RING_BREITE}
        />
        {segmente.map((segment) => (
          <circle
            key={segment.key}
            cx={RING_MITTE}
            cy={RING_MITTE}
            r={RING_R}
            fill="none"
            stroke={segment.color}
            strokeWidth={RING_BREITE}
            strokeDasharray={`${String(segment.strich)} ${String(RING_UMFANG)}`}
            strokeDashoffset={String(-segment.versatz)}
            // Hervorheben heisst: die anderen zurücknehmen. Das Segment selbst
            // wächst nicht, sonst zeigte der Ring unter dem Zeiger eine andere
            // Verteilung als auf dem Papier.
            opacity={hervor && hervor !== segment.key ? 0.35 : undefined}
            onPointerEnter={
              onZeigen
                ? () => {
                    onZeigen(segment.key)
                  }
                : undefined
            }
            onPointerLeave={
              onZeigen
                ? () => {
                    onZeigen('')
                  }
                : undefined
            }
          />
        ))}
      </g>
      <text className={`${praefix}-wert`} x={RING_MITTE} y={68} textAnchor="middle">
        {wert}
      </text>
      <text className={`${praefix}-text`} x={RING_MITTE} y={82} textAnchor="middle">
        {text}
      </text>
    </svg>
  )
}

/**
 * Ein Anteilsbalken: die volle Breite als Grundton, davor der Anteil in der
 * Farbe der Kategorie.
 *
 * preserveAspectRatio="none" ist Absicht - der Balken soll sich auf jede Breite
 * ziehen lassen, ohne dass seine Hoehe mitwaechst. Beschriftet ist er nicht:
 * was er zeigt, steht als Prozentzahl und Betrag in derselben Zeile.
 */
export function ExpenseBar({
  anteil,
  farbe,
  className,
}: {
  anteil: number
  farbe: string
  className: string
}) {
  return (
    <svg
      className={className}
      viewBox={`0 0 ${String(BALKEN_BREITE)} ${String(BALKEN_HOEHE)}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <rect x={0} y={0} width={BALKEN_BREITE} height={BALKEN_HOEHE} fill="var(--atlas)" />
      <rect x={0} y={0} width={anteil} height={BALKEN_HOEHE} fill={farbe} />
    </svg>
  )
}
